// The rate book the Quote Brain prices from (tool T9 and the pricing
// adapter), without node: imports so the browser builds it too.
// rate-book.js adds the content hash a run pins.
//
// The source is the business's own Pricing page, its business_pricing row
// (Peter, 2026-10-27: "this is where the prices come from and it is up to
// the Customer to keep it updated"). pricingPageSource turns that row into
// the rate-card authority shape buildRateBook reads: material_costs,
// labour_rates and equipment rows, each with a stable id and a sell rate,
// and the Pricing page's pricing method. The caller reads the row under the
// business's row security, so one business's rates never reach another's
// run. The offline harness passes pricing-core's bundled card in the same
// shape.
//
// A row prices only when the Pricing page lets it price a quote line:
// isTrustedPricingItem (an AI Review estimate waits for the builder's
// approval), a final charge-out rate (not a cost to mark up), and not GST
// free (the method adds GST to every line). Its sell rate is read as the
// Pricing page shows it: ex GST when the row says so, otherwise inc GST
// (PricingPage.tsx pricingEditorExGstValue). Rows read through apiFetch
// carry camelCase names, so both spellings are read.

import { isHiddenPricingItem, isTrustedPricingItem } from '../../services/pricing-trust.js';
import * as pricingMethodology from '../pricing-core/methodology.js';
import { isPerDayPricingUnit } from '../pricing-core/pricing-unit-semantics.js';

const { resolvePricingMethodology } = pricingMethodology;

// Statutory and overhead components are priced by code (TOOLS.md 4.4), and
// the pre-quote inspection and estimating time sit inside the admin fee
// (pricing-bands.md). They are not offered to the model as rate lines.
const STATUTORY_RE = /\b(?:dbpa|srd|regulated\s+desig[nh]|contract\s+preparation|hbcf|office\s+admin|re-?quoting|physical\s+site\s+inspection)\b/i;

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const words = (s) => norm(s).split(' ').filter((w) => w.length > 1);
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const field = (row, snake, camel) => row?.[snake] ?? row?.[camel];

// Each list's sell field first, as the Pricing page stores it.
const LISTS = [['material_costs', 'cost'], ['labour_rates', 'rate'], ['equipment', 'cost']];

function canonicalTrade(name) {
    return norm(name)
        .replace(/\bbrick ?layer\b/, 'bricklayer')
        .replace(/\bwater ?proofer\b/, 'waterproofer')
        .replace(/\bexcavtor\b/, 'excavator')
        .replace(/\bgeneral\b/, 'semi skilled');
}

// Sorted keys, so the hash does not follow jsonb key order.
function canonical(value) {
    if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
    }
    return JSON.stringify(value ?? null);
}

/** Why this Pricing page row cannot price a quote line, or null. */
function exclusionOf(row) {
    // An AI Review estimate waits for the builder's approval on the Pricing
    // page; a hidden row (statutory, retired, pending review) never prices.
    if (!isTrustedPricingItem(row)) return isHiddenPricingItem(row) ? 'hidden' : 'awaitingApproval';
    if (field(row, 'rate_is_final_charge_out', 'rateIsFinalChargeOut') === false) return 'costInput';
    if (field(row, 'gst_exempt', 'gstExempt') === true) return 'gstFree';
    return null;
}

function sellExGstOf(row, sellField) {
    const amount = Number(row[sellField] ?? row.rate ?? row.cost);
    if (!(amount > 0)) return null;
    const basis = String(field(row, 'charge_out_tax_basis', 'chargeOutTaxBasis') || '').trim().toLowerCase();
    return basis === 'ex_gst' ? amount : round2(amount / 1.1);
}

export function buildRateBook(authority, { hashLines = () => null } = {}) {
    const lines = [];
    const excluded = { awaitingApproval: 0, hidden: 0, costInput: 0, gstFree: 0, noPrice: 0 };
    for (const [list, sellField] of LISTS) {
        for (const row of authority?.[list] || []) {
            const description = String(row?.item || row?.trade || '').trim();
            if (!row?.id || !description) { excluded.noPrice += 1; continue; }
            const why = exclusionOf(row);
            if (why) { excluded[why] += 1; continue; }
            const sell = sellExGstOf(row, sellField);
            if (sell === null) { excluded.noPrice += 1; continue; }
            const unit = String(row.unit || '').trim();
            const sourceNote = field(row, 'source_note', 'sourceNote');
            lines.push({
                rateId: String(row.id),
                description,
                unit,
                sellExGst: sell,
                includesLabour: field(row, 'includes_labour', 'includesLabour') === true,
                // The Pricing page writes a labour day rate as 'Per day' with no
                // flag (tenant-labour-rate.js). A hire row billed per day stays
                // a rate line, never a trade.
                dayRate: list === 'labour_rates'
                    ? isPerDayPricingUnit(unit, field(row, 'is_day_rate', 'isDayRate') === true)
                    : field(row, 'is_day_rate', 'isDayRate') === true || /day rate/i.test(unit),
                hourly: /per hour/i.test(unit),
                sourceTag: sourceNote || 'Rate Book',
                sheet: field(row, 'source_sheet', 'sourceSheet') || '',
                statutory: STATUTORY_RE.test(description),
                ...(list === 'labour_rates' ? { trade: String(row.trade || row.item || '').trim() } : {}),
            });
        }
    }

    const byId = new Map(lines.map((l) => [l.rateId, l]));
    const labour = lines.filter((l) => (l.dayRate || l.hourly) && !l.statutory);
    const methodologyLayer = authority?.pricing_methodology || null;
    // The rates, what each line is (a labour line is matched by its trade name
    // when it is priced) and the pricing method: a change to any of them
    // changes the prices or the lines a build-up can name.
    const hash = hashLines(canonical({
        lines: lines.map((l) => [l.rateId, l.sellExGst, l.unit, l.description, l.trade || '', l.dayRate, l.includesLabour]),
        methodology: methodologyLayer,
    }));

    const publicLine = (l) => ({
        rateId: l.rateId,
        description: l.description,
        unit: l.unit,
        sellExGst: l.sellExGst,
        includesLabour: l.includesLabour,
        dayRate: l.dayRate,
        sourceTag: l.sourceTag,
        sheet: l.sheet,
    });

    function search(query = '', { trade = '', limit = 0 } = {}) {
        const pool = lines.filter((l) => !l.statutory);
        const q = words(`${query} ${trade}`);
        if (q.length === 0) return pool.map(publicLine);
        const scored = pool
            .map((l) => {
                const hay = new Set(words(`${l.description} ${l.unit} ${l.sheet}`));
                const score = q.reduce((s, w) => s + (hay.has(w) ? 2 : [...hay].some((h) => h.startsWith(w) || w.startsWith(h)) ? 1 : 0), 0);
                return { l, score };
            })
            .filter((x) => x.score > 0)
            .sort((a, b) => b.score - a.score);
        const out = scored.map((x) => publicLine(x.l));
        return limit > 0 ? out.slice(0, limit) : out;
    }

    // A labour line names a trade. Day-rate rows win; a trade with only an
    // hourly rate is priced at 8 hours a day (pricing-bands.md: the day rate
    // is the primary basis, so the receipt says when hours were used).
    function dayRate(tradeName) {
        const want = canonicalTrade(tradeName);
        if (!want) return null;
        const match = (pred) => labour.filter((l) => pred(canonicalTrade(l.trade || l.description)));
        const exact = match((t) => t === want);
        const partial = exact.length ? exact : match((t) => t.includes(want) || want.includes(t));
        const day = partial.find((l) => l.dayRate);
        if (day) return { rateId: day.rateId, trade: day.trade || day.description, perDayExGst: day.sellExGst, basis: 'day rate' };
        const hour = partial.find((l) => l.hourly);
        if (hour) return { rateId: hour.rateId, trade: hour.trade || hour.description, perDayExGst: hour.sellExGst * 8, basis: `8 hours at $${hour.sellExGst}/hour` };
        return null;
    }

    const trades = () => [...new Set(labour.map((l) => l.trade || l.description))];

    return {
        id: authority?.authority_id || 'rate-book',
        hash,
        methodologyLayer,
        lineCount: lines.length,
        excluded,
        search,
        get: (rateId) => byId.get(String(rateId || '')) || null,
        dayRate,
        trades,
    };
}

// ── The Pricing page as the source ──────────────────────────────────────────

/** The one pricing method pricing-core prices a Quote Brain run with. */
export const QUOTE_BRAIN_PRICING_METHOD = 'francis-sell-band-v1';

const finite = (v) => v !== null && v !== '' && Number.isFinite(Number(v));
const positive = (v) => finite(v) && Number(v) > 0;
const atLeastZero = (v) => finite(v) && Number(v) >= 0;

/**
 * What the Pricing page's pricing method lacks for the brain to price with
 * it, as plain words, or [] when it is complete. Read with no other layer
 * under it: a missing value never falls back to another business's number
 * (pricing-core's own fallbacks are RJ Bird's).
 */
export function pricingMethodGaps(layer) {
    const m = resolvePricingMethodology(null, layer || {});
    const gaps = [];
    if (!(positive(m.gst_rate))) gaps.push('the GST rate');
    if (!['low', 'medium', 'med_high', 'high'].every((b) => positive(m.bands?.[b]))) gaps.push('the four pricing bands');
    const tiers = m.administration_fee_ex_gst_tiers;
    const tierOk = Array.isArray(tiers) && tiers.length > 0
        && tiers.every((t) => atLeastZero(field(t, 'amount_ex_gst', 'amountExGst')))
        && tiers.some((t) => field(t, 'max_total_inc_gst', 'maxTotalIncGst') == null);
    if (!tierOk) gaps.push('the administration fee tiers');
    if (!positive(m.line_rounding_inc_gst)) gaps.push('the line rounding');
    if (!(positive(m.hbcf?.threshold_inc_gst) && positive(m.hbcf?.premium_rate) && atLeastZero(m.hbcf?.application_fee_ex_gst))) gaps.push('the HBCF threshold, premium rate and application fee');
    const rc = m.regulated_compliance || {};
    // pricing-core reads $0 here as RJ Bird's amount (methodology.js
    // complianceMethodology), so each must be more than $0.
    if (!['engineering_srd_dcd_ex_gst', 'dbpa_registration_ex_gst', 'fair_trading_contract_ex_gst'].every((k) => positive(rc[k]))) gaps.push('the regulated-work compliance amounts (each more than $0)');
    const attendance = m.minimum_attendance_range_inc_gst;
    if (!(Array.isArray(attendance) && atLeastZero(attendance[0]))) gaps.push('the minimum attendance');
    return gaps;
}

// The starter sell-band method, for a business whose Pricing page has none
// (Peter, 2026-11-01: a default for every business, the neutral set, not
// RJ Bird's numbers). NSW's own figures (GST, the HBCF threshold) and generic
// bands, fees and minimum attendance: the Harbourline demo set of 2026-10-30.
// Every business figure differs from RJ Bird's card. The Pricing page shows
// it as starter values, and a business that edits one owns its method.
const STARTER_PRICING_METHOD = {
    id: QUOTE_BRAIN_PRICING_METHOD,
    default_band: 'medium',
    bands: { low: 0.9, medium: 1, med_high: 1.1, high: 1.25 },
    administration_fee_ex_gst_tiers: [
        { max_total_inc_gst: 5000, amount_ex_gst: 250 },
        { max_total_inc_gst: 10000, amount_ex_gst: 500 },
        { max_total_inc_gst: 20000, amount_ex_gst: 800 },
        { max_total_inc_gst: null, amount_ex_gst: 1000 },
    ],
    gst_rate: 0.1,
    line_rounding_inc_gst: 10,
    hbcf: { threshold_inc_gst: 20000, premium_rate: 0.08, application_fee_ex_gst: 1000 },
    regulated_compliance: { engineering_srd_dcd_ex_gst: 6500, dbpa_registration_ex_gst: 2000, fair_trading_contract_ex_gst: 1000 },
    minimum_attendance_range_inc_gst: [1000, 1100],
};

/** A fresh copy of the starter method, so no caller can change the default. */
export function starterPricingMethod() {
    return JSON.parse(JSON.stringify(STARTER_PRICING_METHOD));
}

/**
 * pricing-core's methodology with the Pricing page's method as its only
 * layer, under the name the pricing adapter calls
 * (resolveRjBirdPricingMethodology). Nothing of the bundled card sits under
 * it.
 */
export function pricingPageMethodology(layer) {
    return {
        ...pricingMethodology,
        resolveRjBirdPricingMethodology: (...layers) => resolvePricingMethodology(null, layer || null, ...layers),
    };
}

/**
 * pricingPageSource(row) -> { source } | { refusal: { code, message } }
 * A business's business_pricing row as the rate book's source, or the plain
 * reason the brain cannot price from it (fail closed). `row` is the stored
 * row (snake_case columns) or the API's camelCase response.
 * @returns {{ source?: Record<string, any>, refusal?: { code: string, message: string } }}
 */
export function pricingPageSource(row) {
    const refusal = (code, message) => ({ refusal: { code, message } });
    const lists = Object.fromEntries(LISTS.map(([list]) => {
        const camel = list.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
        const rows = row?.[list] ?? row?.[camel];
        return [list, Array.isArray(rows) ? rows : []];
    }));
    if (!row || LISTS.every(([list]) => lists[list].length === 0)) {
        return refusal('QUOTE_BRAIN_PRICING_MISSING', 'This business has no rates on its Pricing page yet, so QuoteMaker has nothing to price with. Add them on the Pricing page first.');
    }
    const method = pricingPageMethodOf(row.costing_config ?? row.costingConfig);
    if (method.refusal) return method;
    return {
        source: {
            authority_id: 'pricing-page',
            pricing_methodology: method.layer,
            starter_method: method.starter,
            ...lists,
        },
    };
}

/**
 * pricingPageMethodOf(costingConfig) -> { layer, starter } | { refusal }
 * The sell-band method a business prices with, from its Pricing page's
 * costing config: its own method, or the starter method when it has none of
 * its own (none, margin brackets, another id). A method the business set up
 * itself is never filled from another number: a gap is refused.
 */
export function pricingPageMethodOf(config) {
    const own = config?.pricingMethodology ?? config?.pricing_methodology;
    const starter = !(own && typeof own === 'object' && String(own.id || '') === QUOTE_BRAIN_PRICING_METHOD);
    const layer = starter ? starterPricingMethod() : own;
    const gaps = starter ? [] : pricingMethodGaps(layer);
    if (gaps.length) {
        return { refusal: { code: 'QUOTE_BRAIN_PRICING_METHOD', message: `This business's Pricing page method is missing ${gaps.join(', ')}, so QuoteMaker cannot price its quotes.` } };
    }
    return { layer, starter };
}

/**
 * True when this is the business's own sell-band method: its id, or the
 * neutral mark the sell-only Pricing projection keeps in its place for a user
 * without Pricing access (misc-routes.js sellOnlyPricingMethodology), which
 * carries the HBCF and compliance figures but not the bands.
 */
export function isOwnPricingMethod(method) {
    if (!method || typeof method !== 'object') return false;
    return String(method.id || '') === QUOTE_BRAIN_PRICING_METHOD
        || method.own_method === true
        || method.ownMethod === true;
}

// The gaps that matter to a job's HBCF and compliance rows (pricingMethodGaps).
const STATUTORY_GAPS = new Set([
    'the GST rate',
    'the HBCF threshold, premium rate and application fee',
    'the regulated-work compliance amounts (each more than $0)',
]);

/**
 * pricing-core's methodology for the HBCF and compliance rules of a job: the
 * business's own method (isOwnPricingMethod), or the starter method when it
 * has none of its own, or null when its own method lacks one of the figures
 * those rules use. Nothing of RJ Bird's card sits under it, so no business
 * gets another's premium rate, fee or threshold.
 */
export function pricingPageResolvedMethodology(config) {
    const own = config?.pricingMethodology ?? config?.pricing_methodology;
    if (!isOwnPricingMethod(own)) return resolvePricingMethodology(null, starterPricingMethod());
    if (pricingMethodGaps(own).some((gap) => STATUTORY_GAPS.has(gap))) return null;
    return resolvePricingMethodology(null, own);
}

// ── The run's own rates, for a reprice after the run ────────────────────────

/** The key a reprice gives a row's recorded line: rates are the row's own. */
export const receiptKey = (rowKey, idOrTrade) => `${rowKey}\u0000${idOrTrade}`;

/**
 * The rate book brain rows' receipts priced with: each rate line's id,
 * description, unit and sell rate, and each labour line's day rate, as the
 * run recorded them (write-project.js compactReceipt), keyed by the row
 * (receiptKey). Stage 4 prices brain rows again with these, so a reprice
 * moves only what the list change moves, a row kept from an earlier run
 * keeps that run's rates, and no Pricing page is read (a user without
 * Pricing access sees other row ids). `rows` is [{ key, receipt }]. Returns
 * null when a line has no recorded rate (a quote written before the run's
 * rates were recorded).
 */
export function rateBookFromReceipts(rows) {
    const rates = new Map();
    const days = new Map();
    for (const { key, receipt } of rows) {
        for (const l of receipt?.lines || []) {
            if (l.source === 'rate') {
                if (!(Number(l.sellExGst) > 0)) return null;
                rates.set(receiptKey(key, l.rateId), { rateId: String(l.rateId), description: l.description, unit: l.unit, sellExGst: Number(l.sellExGst), includesLabour: l.includesLabour === true });
            } else if (l.source === 'labour') {
                if (!(Number(l.perDayExGst) > 0)) return null;
                days.set(receiptKey(key, l.trade), { trade: l.trade, perDayExGst: Number(l.perDayExGst), basis: l.basis || 'day rate', ...(l.dayRateId ? { rateId: String(l.dayRateId) } : {}) });
            }
        }
    }
    return {
        id: 'run-receipts',
        hash: null,
        methodologyLayer: null,
        get: (rateId) => rates.get(String(rateId || '')) || null,
        dayRate: (trade) => days.get(String(trade)) || null,
        search: () => [],
        trades: () => [...days.values()].map((d) => d.trade),
    };
}
